/**
 * A Shopify rule box: a box whose drop table is not authored but generated from the merchant's
 * rules over the shop's own products, and rebuilt every day. The merchant says which products
 * qualify (tags, vendors, product types, collections, variant options, category attributes, the
 * locations stocking them, a price range) and what margin to keep; the solver
 * (`solveRuleBox` in core) prices the box at a store-friendly price and weights every qualifying
 * product so no product under the minimum return goes in, and the average payout is the price
 * less the margin. Every Shopify box is a rule box.
 */
import { z } from "zod";

/**
 * The share of the price every entry of a rule box is worth at least, in basis points: a shopper
 * who pays $50 wins at least $35 of product. The solver leaves out every product under it. This
 * is the minimum return of a box that sets none (`minReturnBps` null), and the highest one a box
 * may set.
 */
export const RULE_BOX_MIN_RETURN_BPS = 7_000;
/** The lowest minimum return a box may set, in basis points. */
export const RULE_BOX_LOWEST_MIN_RETURN_BPS = 3_000;
/**
 * The minimum return a new box's form starts at, in basis points. Tiered odds (below) give about
 * 47% to the common tier, products worth at most 70% of the price, so they need a minimum return
 * under 70%; at 50% most catalogs fill every tier.
 */
export const RULE_BOX_NEW_MIN_RETURN_BPS = 5_000;

/**
 * The share of the odds each rarity tier of a tiered rule box gets, in basis points, by
 * `getItemRarity`: common, uncommon, rare, epic, 47 / 30 / 20 / 3%. Legendary products are priced
 * per item instead (`RULE_BOX_LEGENDARY_ODDS_BPS`), taken off common. An empty epic tier passes
 * its share to rare, and the two commonest tiers move by a few points at most to land the payout
 * on the margin.
 */
export const RULE_BOX_TIER_ODDS_BPS = [4_700, 3_000, 2_000, 300] as const;

/**
 * The chance each legendary product (worth 6.5× the price or more) of a tiered rule box gets, in
 * basis points: 0.03%, so even a product worth far more than the box costs it little payout.
 */
export const RULE_BOX_LEGENDARY_ODDS_BPS = 3;

/** The most legendary products a tiered rule box holds: the dearest of them. */
export const RULE_BOX_MAX_LEGENDARY_ITEMS = 2;

/**
 * How a solved drop table's odds were set:
 * - `tiered`: each rarity tier the drop table shows at its share of `RULE_BOX_TIER_ODDS_BPS`, and
 *   every entry of a tier at the same chance;
 * - `power_law`: each entry's chance falls with its value, `(value / cheapest) ^ −α`, the
 *   fallback when the products cannot fill the tiers.
 */
export const RULE_BOX_ODDS_STYLES = ["tiered", "power_law"] as const;
export type RuleBoxOddsStyle = (typeof RULE_BOX_ODDS_STYLES)[number];

/** The margin a new rule box keeps, in basis points. */
export const RULE_BOX_DEFAULT_MARGIN_BPS = 2_000;
/** The margin range a merchant may pick. The top has to stay under `1 − min return`. */
export const RULE_BOX_MIN_MARGIN_BPS = 500;
export const RULE_BOX_MAX_MARGIN_BPS = 2_500;

/**
 * The most entries one rule box's drop table holds, and the highest "most items" a merchant may
 * set (`maxItems`).
 */
export const RULE_BOX_MAX_ITEMS = 150;
/**
 * The fewest entries a rule box publishes with, and the lowest "fewest items" a merchant may set
 * (`minItems`).
 */
export const RULE_BOX_MIN_ITEMS = 3;

/** Hour of the day (UTC) the daily refresh runs; the countdown counts to it. */
export const RULE_BOX_REFRESH_HOUR_UTC = 0;

/** The next daily refresh after `now`. */
export function nextRuleBoxRefreshAt(now: Date): Date {
  const next = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), RULE_BOX_REFRESH_HOUR_UTC),
  );
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 1);
  return next;
}

/**
 * The optional access scope that names a shop's locations (and the fulfillment app behind each).
 * Location ids sync without it, so a rule naming locations keeps matching; only picking one needs
 * their names. Requested from the builder's fulfillment filter (`shopify.scopes.request`).
 */
export const SHOPIFY_LOCATIONS_SCOPE = "read_locations";

/**
 * The optional access scope that reads a product's category attributes: the Standard Product
 * Taxonomy's category metafields, whose values are metaobjects. Without it they are not synced.
 * Requested from the builder's category attribute filter.
 */
export const SHOPIFY_CATEGORY_VALUES_SCOPE = "read_metaobjects";

/** The scopes the rule builder may ask for on top of the app's own, as `optional_scopes`. */
export const SHOPIFY_RULE_FILTER_SCOPES = [
  SHOPIFY_LOCATIONS_SCOPE,
  SHOPIFY_CATEGORY_VALUES_SCOPE,
] as const;
export type ShopifyRuleFilterScope = (typeof SHOPIFY_RULE_FILTER_SCOPES)[number];

/** Whether `scopes` (as Shopify reports them) grant `scope`: a `write_` scope grants its `read_`. */
export function hasShopifyScope(scopes: readonly string[], scope: string): boolean {
  return scopes.includes(scope) || scopes.includes(scope.replace(/^read_/, "write_"));
}

const tagList = z.array(z.string().trim().min(1).max(255)).max(50);

/** A list of one kind of Shopify GID (`gid://shopify/Collection/123`). */
function gidList(kind: "Collection" | "Location" | "Metaobject") {
  return z.array(z.string().regex(new RegExp(`^gid://shopify/${kind}/\\d+$`))).max(50);
}

/** A variant option filter: the variant's value for option `name` is one of `values`. */
export const ruleBoxOptionFilterSchema = z.object({
  name: z.string().trim().min(1).max(255),
  values: tagList.min(1),
});
export type RuleBoxOptionFilter = z.infer<typeof ruleBoxOptionFilterSchema>;

/**
 * A category attribute filter: the product's `shopify.{key}` category metafield holds one of
 * `values` (metaobject GIDs, which the builder names).
 */
export const ruleBoxCategoryFilterSchema = z.object({
  key: z.string().trim().min(1).max(64),
  values: gidList("Metaobject").min(1),
});
export type RuleBoxCategoryFilter = z.infer<typeof ruleBoxCategoryFilterSchema>;

/** Whether no two entries of `list` share `key(entry)`, ignoring case. */
function distinctBy<T>(list: T[], key: (entry: T) => string): boolean {
  return new Set(list.map((entry) => key(entry).trim().toLowerCase())).size === list.length;
}

/**
 * Which of the shop's products qualify. Every set filter must match, and an empty list does not
 * filter: `includeTags` matches a product carrying any one of them, `excludeTags` drops a product
 * carrying any one of them. Prices are per variant, in cents, inclusive. The rest name Shopify's
 * own records by id, so a rename never changes a rule:
 * - `collections`: the product is in any one of them;
 * - `locations`: the variant is stocked at (has an inventory level at) any one of them, the
 *   merchant's own or a fulfillment app's; Shopify still routes the order;
 * - `options`: for each option, the variant's value is one of its values (Size M or L, and Color
 *   Black); a variant without that option fails it;
 * - `categoryAttributes`: for each attribute, the product holds one of its values.
 *
 * A rule naming a collection, location or value that is gone keeps it and matches nothing through
 * it; it is never dropped, because an emptied list would put every product in the box.
 */
export const ruleBoxRulesSchema = z
  .object({
    includeTags: tagList.default([]),
    excludeTags: tagList.default([]),
    vendors: tagList.default([]),
    productTypes: tagList.default([]),
    minPriceCents: z.number().int().min(0).nullable().default(null),
    maxPriceCents: z.number().int().min(1).nullable().default(null),
    collections: gidList("Collection").default([]),
    locations: gidList("Location").default([]),
    options: z.array(ruleBoxOptionFilterSchema).max(10).default([]),
    categoryAttributes: z.array(ruleBoxCategoryFilterSchema).max(10).default([]),
  })
  .refine(
    (r) => r.minPriceCents == null || r.maxPriceCents == null || r.minPriceCents <= r.maxPriceCents,
    { message: "minPriceCents must not exceed maxPriceCents", path: ["maxPriceCents"] },
  )
  .refine((r) => distinctBy(r.options, (o) => o.name), {
    message: "each option is filtered once",
    path: ["options"],
  })
  .refine((r) => distinctBy(r.categoryAttributes, (a) => a.key), {
    message: "each category attribute is filtered once",
    path: ["categoryAttributes"],
  });
export type RuleBoxRules = z.infer<typeof ruleBoxRulesSchema>;

/**
 * How the price is chosen: estimated by the solver (`auto`), or held at the merchant's own price
 * (`fixed`), which the solver refuses when the qualifying products cannot support it. An `auto`
 * box keeps its current price at a refresh while the products still support it, so the Shopify
 * product's price does not move every day; it is estimated again only when they no longer do.
 */
export const RULE_BOX_PRICE_MODES = ["auto", "fixed"] as const;
export type RuleBoxPriceMode = (typeof RULE_BOX_PRICE_MODES)[number];

const itemCount = z.number().int().min(RULE_BOX_MIN_ITEMS).max(RULE_BOX_MAX_ITEMS);

/**
 * What the merchant saves: the rules, the margin, the price mode and how many items the drop
 * table may hold.
 */
export const ruleBoxConfigSchema = z
  .object({
    rules: ruleBoxRulesSchema,
    marginBps: z
      .number()
      .int()
      .min(RULE_BOX_MIN_MARGIN_BPS)
      .max(RULE_BOX_MAX_MARGIN_BPS)
      .default(RULE_BOX_DEFAULT_MARGIN_BPS),
    priceMode: z.enum(RULE_BOX_PRICE_MODES).default("auto"),
    /** Required with `fixed`, ignored with `auto`. */
    fixedPriceCents: z.number().int().min(100).nullable().default(null),
    /**
     * The fewest entries the drop table may hold: with fewer, the box is refused and shows sold
     * out. Null = `RULE_BOX_MIN_ITEMS`.
     */
    minItems: itemCount.nullable().default(null),
    /**
     * The most entries the drop table holds, however many products qualify; the solver keeps a
     * spread of them. Null = `RULE_BOX_MAX_ITEMS`.
     */
    maxItems: itemCount.nullable().default(null),
    /**
     * The share of the price every entry is worth at least. Null = `RULE_BOX_MIN_RETURN_BPS`, so a
     * box saved before this setting keeps its promise.
     */
    minReturnBps: z
      .number()
      .int()
      .min(RULE_BOX_LOWEST_MIN_RETURN_BPS)
      .max(RULE_BOX_MIN_RETURN_BPS)
      .nullable()
      .default(null),
  })
  .refine((c) => c.priceMode !== "fixed" || c.fixedPriceCents != null, {
    message: "a fixed price needs fixedPriceCents",
    path: ["fixedPriceCents"],
  })
  .refine((c) => c.minItems == null || c.maxItems == null || c.minItems <= c.maxItems, {
    message: "minItems must not exceed maxItems",
    path: ["maxItems"],
  });
export type RuleBoxConfig = z.infer<typeof ruleBoxConfigSchema>;

/** How many entries a rule box's drop table may hold, with the platform bounds for an unset one. */
export interface RuleBoxItemLimits {
  minItems: number;
  maxItems: number;
}

/** A config's minimum return in basis points: the merchant's own, else `RULE_BOX_MIN_RETURN_BPS`. */
export function ruleBoxMinReturnBps(config: Pick<RuleBoxConfig, "minReturnBps">): number {
  return config.minReturnBps ?? RULE_BOX_MIN_RETURN_BPS;
}

/** A config's item limits: the merchant's own, else `RULE_BOX_MIN_ITEMS` / `RULE_BOX_MAX_ITEMS`. */
export function ruleBoxItemLimits(
  config: Pick<RuleBoxConfig, "minItems" | "maxItems">,
): RuleBoxItemLimits {
  return {
    minItems: config.minItems ?? RULE_BOX_MIN_ITEMS,
    maxItems: config.maxItems ?? RULE_BOX_MAX_ITEMS,
  };
}

/**
 * Why the solver could not build a drop table, one member per cause:
 * - `too_few_items`: fewer products match the rules and are in stock than the box's fewest items
 *   (`minItems`, else `RULE_BOX_MIN_ITEMS`);
 * - `no_supported_price`: no store-friendly price has enough products at or above its minimum
 *   return while still paying out the margin;
 * - `fixed_price_unsupported`: the merchant's fixed price is too high or too low for the
 *   qualifying products (`details` names which).
 */
export const RULE_BOX_REFUSALS = [
  "too_few_items",
  "no_supported_price",
  "fixed_price_unsupported",
] as const;
export type RuleBoxRefusal = (typeof RULE_BOX_REFUSALS)[number];

/**
 * A rule box's last refresh: `ok`, or `unavailable` naming why. An unavailable box keeps selling
 * on its last good drop table (a won product that sold out is paid as store credit, as always),
 * and the merchant sees the refusal; a box that never solved has no version and cannot go on sale.
 */
export const RULE_BOX_REFRESH_STATUSES = ["ok", "unavailable"] as const;
export type RuleBoxRefreshStatus = (typeof RULE_BOX_REFRESH_STATUSES)[number];

/** One entry of a solved drop table, as the builder previews it. */
export interface RuleBoxPreviewEntry {
  catalogItemId: string;
  name: string;
  imageUrl: string | null;
  valueCents: number;
  /** Out of 1,000,000. */
  weight: number;
}

/** What `boxes.preview` answers for a config: the estimate, or the refusal. */
export type RuleBoxPreview =
  | {
      ok: true;
      priceCents: number;
      /** The average payout, in cents. */
      evCents: number;
      /** The least any entry is worth, in cents (the box's minimum return of the price). */
      minReturnCents: number;
      /** How the odds were set: equal within each tier, or falling item by item. */
      oddsStyle: RuleBoxOddsStyle;
      /** Products matching the rules and in stock, before the minimum return drops any. */
      matchedCount: number;
      /** Entries, dearest first. */
      entries: RuleBoxPreviewEntry[];
    }
  | { ok: false; refusal: RuleBoxRefusal; matchedCount: number; message: string };

/** A choice the builder offers by id, shown by its name. */
export interface RuleBoxChoice {
  id: string;
  label: string;
}

/** A location the builder offers: the merchant's own, or a fulfillment app's (`serviceName`). */
export interface RuleBoxLocationChoice extends RuleBoxChoice {
  fulfillmentService: string | null;
}

/** A variant option and every value the shop's live variants give it. */
export interface RuleBoxOptionFacet {
  name: string;
  values: string[];
}

/** A category attribute (`shopify.{key}`) and every value the shop's live products hold. */
export interface RuleBoxCategoryFacet {
  key: string;
  label: string;
  values: RuleBoxChoice[];
}

/**
 * The facets the builder offers as choices: every tag, vendor, product type, collection, variant
 * option and category attribute the shop's live products use, and the shop's locations.
 */
export interface RuleBoxFacets {
  tags: string[];
  vendors: string[];
  productTypes: string[];
  /** The cheapest and dearest live variant, in cents; null for an empty catalog. */
  minPriceCents: number | null;
  maxPriceCents: number | null;
  collections: RuleBoxChoice[];
  /** The shop's active locations, its own first; named only once `read_locations` is granted. */
  locations: RuleBoxLocationChoice[];
  /** The shop holds `SHOPIFY_LOCATIONS_SCOPE`, so its locations can be offered. */
  locationsNamed: boolean;
  options: RuleBoxOptionFacet[];
  categoryAttributes: RuleBoxCategoryFacet[];
  /** The shop holds `SHOPIFY_CATEGORY_VALUES_SCOPE`, so category attributes are synced. */
  categoryValuesNamed: boolean;
}

/** What a shopper-facing read adds to a rule box: when it was last rebuilt and when it will be. */
export interface RuleBoxRefreshInfo {
  refreshedAt: string | null;
  nextRefreshAt: string;
}

/**
 * The server seed commitment a shopper-facing box read carries for the drop table's fairness
 * note: the UTC day, and the hash of the seed every reveal that day rolls from. The seed itself
 * is public once the day is over (`GET /v1/fairness/seeds/:date`).
 */
export interface ServerSeedCommitment {
  date: string;
  hash: string;
}

/**
 * What set a rule box refresh off, one member per cause, for its history:
 * - `created`: the merchant saved a new box;
 * - `rules_changed`: the merchant saved different rules, margin, price mode, item limits or
 *   minimum return;
 * - `manual`: "Refresh now" in the embedded admin;
 * - `daily`: the daily refresh after the catalog sync;
 * - `value_change`: the processor revalued an entry (a product's price moved);
 * - `stock_change`: an `inventory_levels/update` took a product out of stock or back in;
 * - `product_change`: a `products/*` webhook changed, added or removed a product of the shop, or
 *   a collection or location change moved it in or out of a rule (`collections/*`,
 *   `inventory_levels/connect` and `disconnect`);
 * - `renamed`: the box was renamed (dashboard or embedded admin). Not a refresh: its outcome is
 *   `unchanged`, its cause is "old name → new name", and it carries no version, price or count.
 */
export const RULE_BOX_HISTORY_TRIGGERS = [
  "created",
  "rules_changed",
  "manual",
  "daily",
  "value_change",
  "stock_change",
  "product_change",
  "renamed",
] as const;
export type RuleBoxHistoryTrigger = (typeof RULE_BOX_HISTORY_TRIGGERS)[number];

/**
 * How a refresh ended: a new version (`published`), the same table as before (`unchanged`), or
 * the solver's refusal (`refused`, the last good version stays on sale).
 */
export const RULE_BOX_HISTORY_OUTCOMES = ["published", "unchanged", "refused"] as const;
export type RuleBoxHistoryOutcome = (typeof RULE_BOX_HISTORY_OUTCOMES)[number];

/**
 * Why a product left a rule box's drop table, one member per cause:
 * - `out_of_stock`: a FIXED-stock variant at zero;
 * - `removed_from_shop`: the variant or product was deleted in Shopify;
 * - `product_unavailable`: the product went DRAFT or ARCHIVED, or its catalog item cannot be
 *   fulfilled;
 * - `no_longer_matches`: the rules no longer match it (the rules changed, or the product's tags,
 *   vendor, type, price, collections, options, category attributes or locations did);
 * - `below_min_return`: worth less than the minimum return at the box's price;
 * - `not_sampled`: more products qualify than the box's most items (`maxItems`), and the solver
 *   kept other products.
 */
export const RULE_BOX_REMOVAL_REASONS = [
  "out_of_stock",
  "removed_from_shop",
  "product_unavailable",
  "no_longer_matches",
  "below_min_return",
  "not_sampled",
] as const;
export type RuleBoxRemovalReason = (typeof RULE_BOX_REMOVAL_REASONS)[number];

/**
 * Why a product joined, one member per cause: `new_product` (a variant first synced since the
 * previous version), `back_in_stock`, `now_matches` (the rules or the product changed so it
 * matches), `now_above_min_return` (the price moved so it clears the minimum return), `sampled`
 * (more products qualify than the box's most items, and the solver now keeps it).
 */
export const RULE_BOX_ADDITION_REASONS = [
  "new_product",
  "back_in_stock",
  "now_matches",
  "now_above_min_return",
  "sampled",
] as const;
export type RuleBoxAdditionReason = (typeof RULE_BOX_ADDITION_REASONS)[number];

/** One product in a history entry, named as it was at the time. */
export interface RuleBoxHistoryItem {
  catalogItemId: string;
  name: string;
  valueCents: number;
}

/** What one refresh changed in the drop table. Empty lists when nothing moved. */
export interface RuleBoxHistoryChanges {
  added: (RuleBoxHistoryItem & { reason: RuleBoxAdditionReason })[];
  removed: (RuleBoxHistoryItem & { reason: RuleBoxRemovalReason })[];
  /** Entries still in the table whose value moved. */
  revalued: { catalogItemId: string; name: string; fromCents: number; toCents: number }[];
}

/** One row of a rule box's history, newest first in a list. */
export interface RuleBoxHistoryEntry {
  id: string;
  at: string;
  trigger: RuleBoxHistoryTrigger;
  outcome: RuleBoxHistoryOutcome;
  /**
   * Who set it off: `merchant` (the embedded admin, with the staff member's Shopify user id when
   * known), `staff` (the Loot OS dashboard), or `system` (the cron or a webhook).
   */
  actor: { kind: "merchant" | "staff" | "system"; label: string | null };
  /**
   * The event that set it off, when there is one: the product or variant a webhook named (its
   * title), the config fields the merchant changed, or a rename's "old name → new name".
   */
  cause: string | null;
  fromVersion: number | null;
  toVersion: number | null;
  priceFromCents: number | null;
  priceToCents: number | null;
  itemCountFrom: number | null;
  itemCountTo: number | null;
  changes: RuleBoxHistoryChanges;
  /** The solver's message on a refusal. */
  refusalMessage: string | null;
}

/** A page of history: `nextCursor` is passed back to read older entries, null at the end. */
export interface RuleBoxHistoryPage {
  entries: RuleBoxHistoryEntry[];
  nextCursor: string | null;
}
